import React, { useState, useEffect, useMemo } from 'react';
import HeaderRouter from '../components/HeaderRouter';
import { Filter } from 'lucide-react';
import { fetchNewLeaderboard } from '../api/studentApi';
import './LeaderBoardPageNew.css';

const LeaderBoardPageNew = ({ showHeader = true }) => {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrade, setSelectedGrade] = useState('ALL');

  useEffect(() => {
    let isMounted = true;
    const fetchLeaderboard = async () => {
      try {
        const response = await fetchNewLeaderboard();
        if (isMounted) {
          setLeaders(response.data || []);
        }
      } catch (error) {
        console.error('Error fetching ranking statistics:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchLeaderboard();
    return () => {
      isMounted = false;
    };
  }, []);

  const availableGrades = useMemo(() => {
    const gradesMap = new Map();
    leaders.forEach((item) => {
      if (item.gradeId && item.gradeName) {
        gradesMap.set(item.gradeId, item.gradeName);
      }
    });

    return Array.from(gradesMap.entries()).map(([id, name]) => ({
      id,
      name,
    }));
  }, [leaders]);

  const filteredLeaders = useMemo(() => {
    if (selectedGrade === 'ALL') return leaders;
    return leaders.filter((item) => String(item.gradeId) === String(selectedGrade));
  }, [leaders, selectedGrade]);

  const topThree = useMemo(() => filteredLeaders.slice(0, 3), [filteredLeaders]);
  const remainingLeaders = useMemo(() => filteredLeaders.slice(3, 10), [filteredLeaders]);

  const getInitials = (username = '') => {
    if (!username) return 'EX';
    const parts = username.split(/[._\s]+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return username.slice(0, 2).toUpperCase();
  };

  return (
    <div className={`leaderboard-wrapper ${!showHeader ? 'embedded' : ''}`}>
      {showHeader && <HeaderRouter />}

      <main className="leaderboard-container">
        <div className="leaderboard-header">
          <div className="title-area">
            <h1>
              <span className="emoji-icon" role="img" aria-label="trophy">🏆</span> Meet our Champions
            </h1>
            <p className="subtitle">Top performers across quizzes and points</p>
          </div>

          <div className="filter-dropdown-wrapper">
            <Filter size={18} className="filter-icon" />
            <select
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e.target.value)}
              className="grade-select"
              aria-label="Filter champions by grade"
            >
              <option value="ALL">All Grades</option>
              {availableGrades.map((grade) => (
                <option key={grade.id} value={grade.id}>
                  {grade.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="leaderboard-loading">
            <div className="spinner" />
            <p>Loading Champions...</p>
          </div>
        ) : filteredLeaders.length === 0 ? (
          <div className="empty-leaderboard">
            <p>No champions found for this grade level yet!</p>
          </div>
        ) : (
          <React.Fragment>
            <div className="podium-section">
              {/* Silver (Rank 2) */}
              {topThree[1] && (
                <div className="podium-card silver">
                  <div className="medal-badge silver-medal">
                    <span className="medal-emoji" role="img" aria-label="silver medal">🥈</span>
                    <span className="rank-tag">2nd</span>
                  </div>
                  <div className="podium-avatar">{getInitials(topThree[1].username)}</div>
                  <h3 className="podium-username" title={topThree[1].username}>
                    {topThree[1].username}
                  </h3>
                  <span className="podium-grade">{topThree[1].gradeName}</span>
                  <div className="podium-points">
                    <span>{topThree[1].points.toLocaleString()}</span> pts
                  </div>
                  <div className="podium-stats">
                    <span title="Accuracy">🎯 {topThree[1].accuracy}%</span>
                    <span title="Quizzes Completed">✍️ {topThree[1].totalQuizzes}</span>
                    <span title="Badges Earned">🎖️️ {topThree[1].badgesCount}</span>
                  </div>
                </div>
              )}

              {/* Gold (Rank 1) */}
              {topThree[0] && (
                <div className="podium-card gold">
                  <div className="crown-label">CHAMPION</div>
                  <div className="medal-badge gold-medal">
                    <span className="medal-emoji" role="img" aria-label="gold medal">🥇</span>
                    <span className="rank-tag">1st</span>
                  </div>
                  <div className="podium-avatar">{getInitials(topThree[0].username)}</div>
                  <h3 className="podium-username" title={topThree[0].username}>
                    {topThree[0].username}
                  </h3>
                  <span className="podium-grade">{topThree[0].gradeName}</span>
                  <div className="podium-points">
                    <span>{topThree[0].points.toLocaleString()}</span> pts
                  </div>
                  <div className="podium-stats">
                    <span title="Accuracy">🎯 {topThree[0].accuracy}%</span>
                    <span title="Quizzes Completed">✍️ {topThree[0].totalQuizzes}</span>
                    <span title="Badges Earned">🎖️ {topThree[0].badgesCount}</span>
                  </div>
                </div>
              )}

              {/* Bronze (Rank 3) */}
              {topThree[2] && (
                <div className="podium-card bronze">
                  <div className="medal-badge bronze-medal">
                    <span className="medal-emoji" role="img" aria-label="bronze medal">🥉</span>
                    <span className="rank-tag">3rd</span>
                  </div>
                  <div className="podium-avatar">{getInitials(topThree[2].username)}</div>
                  <h3 className="podium-username" title={topThree[2].username}>
                    {topThree[2].username}
                  </h3>
                  <span className="podium-grade">{topThree[2].gradeName}</span>
                  <div className="podium-points">
                    <span>{topThree[2].points.toLocaleString()}</span> pts
                  </div>
                  <div className="podium-stats">
                    <span title="Accuracy">🎯 {topThree[2].accuracy}%</span>
                    <span title="Quizzes Completed">✍️ {topThree[2].totalQuizzes}</span>
                    <span title="Badges Earned">🎖️ {topThree[2].badgesCount}</span>
                  </div>
                </div>
              )}
            </div>

            {/* List Section (Ranks 4-10) */}
            {remainingLeaders.length > 0 && (
              <div className="leaderboard-table-container">
                <div className="table-header-row">
                  <div className="col-header rank">Rank</div>
                  <div className="col-header profile">Profile</div>
                  <div className="col-header name-grade">Name & Grade</div>
                  <div className="col-header stats">Acc · Quiz · Badges</div>
                  <div className="col-header points">Points</div>
                </div>

                <div className="table-body">
                  {remainingLeaders.map((student, index) => (
                    <div className="table-row" key={student.id}>
                      <div className="col-cell rank">
                        <span className="rank-num">#{index + 4}</span>
                      </div>

                      <div className="col-cell profile">
                        <div className="avatar-circle">{getInitials(student.username)}</div>
                      </div>

                      <div className="col-cell name-grade">
                        <span className="student-name">{student.username}</span>
                        <span className="student-grade">{student.gradeName}</span>
                      </div>

                      <div className="col-cell stats">
                        <span className="stat-item accuracy" title="Accuracy">
                          🎯 {student.accuracy}%
                        </span>
                        <span className="stat-item quizzes" title="Quizzes Completed">
                          ✍️ {student.totalQuizzes}
                        </span>
                        <span className="stat-item badges" title="Badges">
                          🎖️ {student.badgesCount}
                        </span>
                      </div>

                      <div className="col-cell points">
                        <strong>{student.points.toLocaleString()}</strong>
                        <span className="pts-label">pts</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </React.Fragment>
        )}
      </main>
    </div>
  );
};

export default LeaderBoardPageNew;